import { useCallback, useEffect, useMemo, useState } from "react";
import * as api from "../../api/client";
import type { AdminJob, CatalogCard } from "../../api/client";
import { pixelTheme, type Theme } from "../../theme/pixel";
import { defaultBtn, primaryBtn, withBtnStyle } from "../../theme/styles";
import { AnimatedEllipsis } from "./AnimatedEllipsis";
import { Inspector } from "../Inspector";
import { SortHeader, thStyle, tdStyle, useSweepJob } from "./balanceShared";

// Card Power Rankings: every card's Win-Participation-Rate (random-pile
// vs random-pile) plus a faction-adjusted score (raw power minus the
// faction median). The full sweep is a CPU-exclusive background job;
// the per-row RE-CHECK button runs a single-card eval on demand.

function jobIsNpc(j: AdminJob): boolean {
  const v = j.args?.include_npc;
  return v === true || v === "true";
}

type SortCol = "power" | "adj" | "name" | "faction";

type Variant = "player" | "npc";

const VARIANT_LABEL: Record<Variant, string> = {
  player: "PLAYER CARDS",
  npc: "NPC CARDS",
};

export function BalanceTab() {
  const theme = pixelTheme;
  const [inspectedCard, setInspectedCard] = useState<CatalogCard | null>(null);
  const [inspectError, setInspectError] = useState<string | null>(null);
  const [variant, setVariant] = useState<Variant>("player");

  const inspect = useCallback(async (cardId: string) => {
    try {
      // Admin variant bypasses the encountered-by-user catalog gate so
      // any card in the rankings can be inspected even if the admin
      // hasn't personally played against it.
      const card = await api.getAdminCardCatalog(cardId);
      setInspectedCard(card);
    } catch (err) {
      setInspectError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        height: "100%",
        minHeight: 0,
      }}
    >
      <div style={{ display: "flex", gap: 4 }}>
        {(["player", "npc"] as const).map((v) => {
          const active = v === variant;
          return (
            <button
              key={v}
              onClick={() => setVariant(v)}
              {...withBtnStyle(defaultBtn(theme), {
                fontSize: 11,
                padding: "4px 10px",
                background: active ? theme.accent : "transparent",
                color: active ? theme.bgDeep : theme.ink,
              })}
            >
              {VARIANT_LABEL[v]}
            </button>
          );
        })}
      </div>
      {inspectError && (
        <div style={{ color: theme.accent, fontSize: 11 }}>{inspectError}</div>
      )}
      {/* Remount on variant switch — independent fetch + sweep state per
          sub-tab. Cheaper than threading a discriminator through every
          state hook. */}
      <CardPowerRankings
        key={variant}
        theme={theme}
        variant={variant}
        onInspect={(id) => void inspect(id)}
      />
      <Inspector
        card={inspectedCard}
        theme={theme}
        onClose={() => setInspectedCard(null)}
      />
    </div>
  );
}

function CardPowerRankings({
  theme,
  variant,
  onInspect,
}: {
  theme: Theme;
  variant: Variant;
  onInspect: (cardId: string) => void;
}) {
  const isNpc = variant === "npc";
  const fetchPower = isNpc ? api.listCardPowerNpc : api.listCardPower;
  const enqueueSweep = isNpc ? api.enqueueCardPowerNpcSweep : api.enqueueCardPowerSweep;
  const [power, setPower] = useState<api.CardPower | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [factionFilter, setFactionFilter] = useState<Set<string>>(new Set());
  const [sortCol, setSortCol] = useState<SortCol>("adj");
  const [sortDesc, setSortDesc] = useState(true);
  // Card-id → in-progress flag for the per-row RE-CHECK button.
  const [rechecking, setRechecking] = useState<Set<string>>(new Set());
  // Per-card overrides written by RE-CHECK: card_id → fresh
  // {winRate, adj}. Takes precedence over the bulk sweep numbers for
  // that row so the admin's re-eval result is visible immediately
  // without waiting for the next sweep.
  const [recheckResults, setRecheckResults] = useState<
    Map<string, { winRate: number; adj: number }>
  >(new Map());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = await fetchPower();
      setPower(p);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [fetchPower]);

  useEffect(() => {
    void load();
  }, [load]);

  // Player vs NPC sweeps share the same job kind — distinguish on
  // `args.include_npc` so the two sub-tabs don't claim each other's
  // in-flight job.
  const sweep = useSweepJob({
    matches: (j) => j.kind === "card_power_sweep" && jobIsNpc(j) === isNpc,
    enqueue: enqueueSweep,
    onSuccess: async () => {
      // Fresh sweep invalidates any prior re-check overrides.
      setRecheckResults(new Map());
      await load();
    },
    onError: (err) =>
      setError(err instanceof Error ? err.message : String(err)),
  });

  const onRunSweep = useCallback(() => {
    setError(null);
    void sweep.run();
  }, [sweep]);

  // Per-faction median power, computed locally so we don't depend on
  // the server hand-shipping it (admin can re-check a card and we
  // immediately recompute the deviation against the same median).
  const factionMedian = useMemo(() => {
    const out = new Map<string, number>();
    if (!power) return out;
    const byFaction = new Map<string, number[]>();
    for (const c of power.cards) {
      if (c.win_rate == null || !c.card_faction) continue;
      const arr = byFaction.get(c.card_faction) ?? [];
      arr.push(c.win_rate);
      byFaction.set(c.card_faction, arr);
    }
    for (const [f, vals] of byFaction) {
      vals.sort((a, b) => a - b);
      const mid = vals.length % 2 === 1
        ? vals[Math.floor(vals.length / 2)]
        : (vals[vals.length / 2 - 1] + vals[vals.length / 2]) / 2;
      out.set(f, mid);
    }
    return out;
  }, [power]);

  // Bulk-sweep power + adj per card_id, with re-check overrides applied.
  const powerByCard = useMemo(() => {
    const map = new Map<
      string,
      { winRate: number | null; adj: number | null; inclusions: number }
    >();
    if (!power) return map;
    for (const c of power.cards) {
      const override = recheckResults.get(c.card_id);
      const winRate = override?.winRate ?? c.win_rate;
      const median = c.card_faction ? factionMedian.get(c.card_faction) : undefined;
      const adj =
        override?.adj ??
        (winRate != null && median != null ? (winRate - median) * 100 : null);
      map.set(c.card_id, { winRate, adj, inclusions: c.inclusions });
    }
    return map;
  }, [power, factionMedian, recheckResults]);

  const onRecheck = useCallback(async (cardId: string) => {
    setRechecking((prev) => new Set(prev).add(cardId));
    try {
      const report = await api.evalCardBalance(cardId);
      setRecheckResults((prev) => {
        const next = new Map(prev);
        next.set(cardId, { winRate: report.win_rate, adj: report.adj_pp });
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRechecking((prev) => {
        const next = new Set(prev);
        next.delete(cardId);
        return next;
      });
    }
  }, []);

  const allFactions = useMemo(() => {
    if (!power) return [];
    const set = new Set<string>();
    for (const c of power.cards) {
      if (c.card_faction) set.add(c.card_faction);
    }
    return Array.from(set).sort();
  }, [power]);

  const filteredSorted = useMemo(() => {
    if (!power) return [];
    let rows = power.cards.slice();
    if (factionFilter.size > 0) {
      rows = rows.filter(
        (r) => r.card_faction && factionFilter.has(r.card_faction),
      );
    }
    const cmp = (a: api.CardPowerEntry, b: api.CardPowerEntry): number => {
      const direction = sortDesc ? -1 : 1;
      switch (sortCol) {
        case "power": {
          const pa = powerByCard.get(a.card_id)?.winRate ?? null;
          const pb = powerByCard.get(b.card_id)?.winRate ?? null;
          if (pa == null && pb == null) return 0;
          if (pa == null) return 1;
          if (pb == null) return -1;
          return direction * (pa - pb);
        }
        case "adj": {
          const aa = powerByCard.get(a.card_id)?.adj ?? null;
          const ab = powerByCard.get(b.card_id)?.adj ?? null;
          if (aa == null && ab == null) return 0;
          if (aa == null) return 1;
          if (ab == null) return -1;
          return direction * (aa - ab);
        }
        case "name":
          return direction * a.card_id.localeCompare(b.card_id);
        case "faction":
          return (
            direction *
            (a.card_faction ?? "").localeCompare(b.card_faction ?? "")
          );
        default:
          return 0;
      }
    };
    rows.sort(cmp);
    return rows;
  }, [power, factionFilter, sortCol, sortDesc, powerByCard]);

  const toggleFaction = useCallback((f: string) => {
    setFactionFilter((prev) => {
      const next = new Set(prev);
      if (next.has(f)) next.delete(f);
      else next.add(f);
      return next;
    });
  }, []);

  const onHeaderClick = useCallback((col: SortCol) => {
    setSortCol((prev) => {
      if (prev === col) {
        setSortDesc((d) => !d);
        return col;
      }
      setSortDesc(col === "power" || col === "adj");
      return col;
    });
  }, []);

  if (loading) {
    return <AnimatedEllipsis label="Loading card rankings" />;
  }
  if (error && !power) {
    return <div style={{ color: theme.accent }}>{error}</div>;
  }

  const sweepRunning = sweep.running;
  const sweepJob = sweep.job;
  const noSweepYet = power?.no_sweep_yet ?? false;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        flex: 1,
        minHeight: 0,
      }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
        <button
          onClick={onRunSweep}
          disabled={sweepRunning}
          {...withBtnStyle(primaryBtn(theme), { fontSize: 11, padding: "4px 10px" })}
        >
          {sweepRunning
            ? sweepJob?.status === "running"
              ? "SWEEP RUNNING…"
              : "SWEEP QUEUED…"
            : "RUN SWEEP"}
        </button>
        {power && !power.no_sweep_yet && (
          <span style={{ color: theme.inkFaint, fontSize: 11 }}>
            {power.cards.length} cards · {power.games_played ?? "?"} games
            {power.elapsed_secs != null && <> · {power.elapsed_secs.toFixed(0)}s</>}
          </span>
        )}
        {error && <span style={{ color: theme.accent, fontSize: 11 }}>{error}</span>}
      </div>

      {allFactions.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          <span style={{ color: theme.inkFaint, fontSize: 11, alignSelf: "center" }}>
            FILTER:
          </span>
          {allFactions.map((f) => (
            <button
              key={f}
              onClick={() => toggleFaction(f)}
              {...withBtnStyle(defaultBtn(theme), {
                fontSize: 11,
                padding: "2px 8px",
                background: factionFilter.has(f) ? theme.accent : "transparent",
                color: factionFilter.has(f) ? theme.bgDeep : theme.ink,
              })}
            >
              {f}
            </button>
          ))}
          {factionFilter.size > 0 && (
            <button
              onClick={() => setFactionFilter(new Set())}
              {...withBtnStyle(defaultBtn(theme), { fontSize: 11, padding: "2px 8px" })}
            >
              clear
            </button>
          )}
        </div>
      )}

      {noSweepYet ? (
        <div style={{ color: theme.inkFaint, fontSize: 13 }}>
          No sweep yet. Click <strong>RUN SWEEP</strong> to score every card.
        </div>
      ) : (
        <div
          style={{
            minHeight: 0,
            overflowY: "auto",
            border: `1px solid ${theme.panelEdge}`,
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead
              style={{
                position: "sticky",
                top: 0,
                background: theme.bgDeep,
                zIndex: 1,
              }}
            >
              <tr>
                <SortHeader
                  theme={theme}
                  active={sortCol === "name"}
                  onClick={() => onHeaderClick("name")}
                >
                  CARD
                </SortHeader>
                <SortHeader
                  theme={theme}
                  active={sortCol === "faction"}
                  onClick={() => onHeaderClick("faction")}
                >
                  FACTION
                </SortHeader>
                <SortHeader
                  theme={theme}
                  active={sortCol === "power"}
                  onClick={() => onHeaderClick("power")}
                  align="right"
                >
                  POWER % {sortCol === "power" ? (sortDesc ? "↓" : "↑") : ""}
                </SortHeader>
                <SortHeader
                  theme={theme}
                  active={sortCol === "adj"}
                  onClick={() => onHeaderClick("adj")}
                  align="right"
                >
                  ADJ {sortCol === "adj" ? (sortDesc ? "↓" : "↑") : ""}
                </SortHeader>
                <th style={thStyle(theme, "right")}>—</th>
              </tr>
            </thead>
            <tbody>
              {filteredSorted.map((c) => {
                const stats = powerByCard.get(c.card_id) ?? null;
                return (
                  <RankingsRow
                    key={c.card_id}
                    entry={c}
                    power={stats}
                    theme={theme}
                    rechecking={rechecking.has(c.card_id)}
                    onRecheck={() => onRecheck(c.card_id)}
                    onInspect={() => onInspect(c.card_id)}
                    showRecheck={!isNpc}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RankingsRow({
  entry,
  power,
  theme,
  rechecking,
  onRecheck,
  onInspect,
  showRecheck,
}: {
  entry: api.CardPowerEntry;
  power: { winRate: number | null; adj: number | null; inclusions: number } | null;
  theme: Theme;
  rechecking: boolean;
  onRecheck: () => void;
  onInspect: () => void;
  showRecheck: boolean;
}) {
  const powerPct = power?.winRate != null ? power.winRate * 100 : null;
  const powerColor =
    powerPct == null
      ? theme.inkFaint
      : Math.abs(powerPct - 50) <= 3
        ? theme.ink
        : powerPct < 50
          ? "#c54a3b"
          : "#3aa256";

  const adj = power?.adj ?? null;
  const adjColor =
    adj == null
      ? theme.inkFaint
      : Math.abs(adj) <= 2
        ? theme.ink
        : adj < 0
          ? "#c54a3b"
          : "#3aa256";

  return (
    <tr style={{ borderBottom: `1px solid ${theme.panelEdge}` }}>
      <td style={tdStyle(theme)}>
        <button
          onClick={onInspect}
          style={{
            background: "transparent",
            border: "none",
            padding: 0,
            cursor: "pointer",
            color: theme.ink,
            fontFamily: "inherit",
            fontSize: "inherit",
            textDecoration: "underline",
            textDecorationStyle: "dotted",
          }}
        >
          {entry.card_id}
        </button>
      </td>
      <td style={tdStyle(theme)}>{entry.card_faction ?? "—"}</td>
      <td
        style={{
          ...tdStyle(theme),
          textAlign: "right",
          color: powerColor,
          fontWeight: 600,
        }}
        title={power ? `inclusions: ${power.inclusions}` : undefined}
      >
        {powerPct == null ? "—" : `${powerPct.toFixed(1)}%`}
      </td>
      <td
        style={{
          ...tdStyle(theme),
          textAlign: "right",
          color: adjColor,
          fontWeight: 600,
        }}
        title="Faction-adjusted power: raw power − faction median, in pp"
      >
        {adj == null ? "—" : `${adj >= 0 ? "+" : ""}${adj.toFixed(1)}`}
      </td>
      <td style={{ ...tdStyle(theme), textAlign: "right" }}>
        {showRecheck && (
          <button
            onClick={onRecheck}
            disabled={rechecking}
            {...withBtnStyle(defaultBtn(theme), { fontSize: 10, padding: "2px 6px" })}
          >
            {rechecking ? "…" : "RE-CHECK"}
          </button>
        )}
      </td>
    </tr>
  );
}

